/**
 * Author: Vika Aqordi
 * Created on 13-10-2025-09h-37m
 * Github: https://github.com/aqordeon
 * Copyright: 2025
 */

import axios from "axios"
import { set } from "lodash-es"
import Cookies from "js-cookie"
import { usePage } from "@inertiajs/vue3"
import { resolveIsLoggedIn } from "@/Composables/irisAuthFlag"

let firstHitRequest: Promise<unknown> | null = null

const afterFirstHitSessionIsSet = (callback: () => void) => {
  setTimeout(() => {
    Promise.allSettled([firstHitRequest]).then(callback)
  }, 0)
}

export const initialiseIrisVarnish = async (layoutStore) => {
  const layout = layoutStore()
  let storageIris = {}
  if (typeof window !== "undefined") {
    storageIris = JSON.parse(localStorage.getItem("iris") || "{}")  // Get layout from localStorage
    layout.iris.is_logged_in = resolveIsLoggedIn()

    // Restore the cached user snapshot immediately so logged-in chrome (topbar
    // greeting, offer label) renders at first paint instead of popping in when
    // the first-hit response lands; the fetch below refreshes the same fields.
    if (layout.iris.is_logged_in && storageIris?.is_logged_in) {
      if (storageIris.iris_variables) {
        layout.iris_variables = storageIris.iris_variables
      }
      if (storageIris.offer_data) {
        layout.offer_data = storageIris.offer_data
      }
      if (storageIris.offer_meters) {
        layout.offer_meters = storageIris.offer_meters
      }
    }
  }

  const isAppRoute = window.location.pathname.startsWith("/app")
  const selectedUrl = !isAppRoute ? "/json/first-hit" : "/app/json/first-hit"  // HasIrisUserData
  const currentUrl = new URL(window.location.href)
  const headers = {
    "X-Traffic-Sources": currentUrl.search?.replace(/^\?/, "") || "", //todo: review this because this headers are no set anymore use url queries instead
    // The page HTML comes from Varnish, so the backend never sees the click that landed the visitor
    // here and this fetch's own Referer is just the storefront. document.referrer is the only place
    // the external referrer survives, so forward it or organic traffic can never be attributed.
    // Paid clicks are handled separately: their click ids ride along in this request's Referer.
    "X-Original-Referer": document.referrer || "",
    "X-Requested-With": "XMLHttpRequest"
  }

  // Fetch: auth_data (GetIrisFirstHitData)
  const getVarnishData = async () => {
    try {
      set(layout, ["iris_varnish", "isFetching"], true)
      firstHitRequest = axios.get(selectedUrl, { headers })
      const response = await firstHitRequest
      set(layout, ["iris_varnish", "isFetching"], false)

      // console.log('Iris Varnish', response.data)
      return response.data
    } catch (error) {
      if (error?.status === 403) {
        localStorage.setItem("iris", JSON.stringify({
          ...storageIris,
          is_logged_in: false,
          iris_variables: null,
          offer_data: null,
          offer_meters: null
        }))
        layout.iris.is_logged_in = false
      }
      // console.error('Error fetching first hit:', error)
    } finally {
      set(layout, ["iris_varnish", "isFetching"], false)
    }
  }

  const varnish = await getVarnishData()
  if (!varnish) return

  /* Written before anything branches on the visitor being logged in. The logged-out branch below
     returns early, so keeping this at the foot of the function meant an anonymous visitor - the only
     kind who can still be acquired - had their touch cookie computed by the backend, sent to the
     browser and thrown away. Every touch we held came from somebody already logged in, which is why
     no registration was ever credited to a channel. */
  if (varnish?.traffic_source_cookies) {
    for (const [key, cookieData] of Object.entries(varnish.traffic_source_cookies)) {
      if (cookieData?.value) {
        // js-cookie's third argument is an attributes object; a bare number is silently ignored and
        // the cookie dies with the session. duration arrives in minutes, expires wants days.
        Cookies.set(key, cookieData.value, { expires: cookieData.duration / (60 * 24) })
      }
    }
  }


  // --- Handle logged-out ---
  if (!varnish.is_logged_in) {
    localStorage.setItem("iris", JSON.stringify({
      ...storageIris,
      is_logged_in: false,
      iris_variables: varnish?.variables ?? null,
      offer_data: null,
      offer_meters: varnish?.offer_meters ?? null
    }))

    layout.is_blocked = varnish.is_blocked

    layout.user = varnish.auth?.user || null
    if (layout.user?.customerSalesChannels) {
      layout.user.customerSalesChannels = null
    }

    layout.iris.is_logged_in = false
    layout.iris.customer = null
    return
  }

  // --- Handle logged-in ---
  if (varnish.is_logged_in) {
    // Data: Iris Variables
    if (varnish?.variables) {
      layout.iris_variables = varnish.variables
    }

    layout.is_blocked = varnish.is_blocked

    // Data: Gold Reward
    if (varnish?.offer_data) {
      layout.offer_data = varnish.offer_data
    }

    if (varnish?.offer_meters) {
      layout.offer_meters = varnish.offer_meters
    }

    localStorage.setItem("iris", JSON.stringify({
      ...storageIris,
      is_logged_in: true,
      iris_variables: varnish.variables,
      offer_data: varnish.offer_data ?? null,
      offer_meters: varnish.offer_meters ?? null
    }))

    layout.user = varnish.auth?.user || null
    layout.user.gr_data = varnish.gr_data || null
    if (varnish.auth?.customerSalesChannels) {
      layout.user.customerSalesChannels = varnish.auth.customerSalesChannels
    }

    layout.iris.is_logged_in = true
    layout.iris.customer = varnish.customer ?? null
  }

}


export const initialiseIrisVarnishCustomerData = async (layout) => {
  const isAppRoute = window.location.pathname.startsWith("/app")
  const selectedUrl = !isAppRoute ? "/json/ecom-customer-data" : "/json/ecom-customer-data"
  const currentUrl = new URL(window.location.href)
  const headers = {
    "X-Traffic-Sources": currentUrl.search?.replace(/^\?/, "") || "", //todo: review this because this headers are no set anymore use url queries instead
    // The page HTML comes from Varnish, so the backend never sees the click that landed the visitor
    // here and this fetch's own Referer is just the storefront. document.referrer is the only place
    // the external referrer survives, so forward it or organic traffic can never be attributed.
    // Paid clicks are handled separately: their click ids ride along in this request's Referer.
    "X-Original-Referer": document.referrer || "",
    "X-Requested-With": "XMLHttpRequest"
  }

  // Fetch: auth_data (GetIrisFirstHitData)
  const getVarnishData = async () => {
    try {
      const response = await axios.get(selectedUrl, { headers })
      set(layout, ["iris_varnish", "isFetching"], false)

      return response.data
    } catch (error) {
      // console.error('Error fetching auth_data:', error)
    } finally {
      set(layout, ["iris_varnish", "isFetching"], false)
    }
  }

  const varnish = await getVarnishData()

  if (!varnish) {
    return
  }

  if (varnish?.offer_meters) {
    layout.offer_meters = varnish.offer_meters
  }

  if (varnish?.variables) {
    layout.iris_variables = { ...layout.iris_variable, ...varnish?.variables }
  }
}


export const recordWebsiteHit = () => {
  const isRetina = window.location.pathname.startsWith("/app")

  const headers = {
    "X-Requested-With": "XMLHttpRequest",
  }

  const hit = {
      original_route: route().current(),
      original_params: route().params,
      webpage_id: usePage().props.webpage_id,
      original_referer: document.referrer,
      analytics_webpage: usePage().props.webpage_slug,
      analytics_page_title: document.title,
      analytics_app: isRetina ? "retina" : "iris",
  }

  // Fire-and-forget request used only to record hit analytics.
  afterFirstHitSessionIsSet(() => {
    void axios.post("/analytics/hit", hit, { headers }).catch(() => {})
  })
}
